import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SubHeader from "components/common/SubHeader";
import MultiImageUpload from "components/common/MultiImageUpload";
import RichTextEditor from "components/common/RichTextEditor";
import api from "utils/api";
import { useSnackbar } from "contexts/SnackbarContext";
import { CURRENCY_OPTIONS, getCurrencyConfig, calcDepositFromBalance, formatAmountInput, formatAmountDisplay } from "utils/currency";
import { getMyInstructorProfile, getPublicInstructors } from "../../api/instructor";
import { getTravelProduct, updateTravelProduct, getPendingTravelDetail } from "../../api/travel";
import "assets/scss/pages/AdminRegistTour.scss";

const MOCK = {
    tour_type: 'LIMITED',// LIMITED or ALWAYS
    name: "",
    price: 0, // 예약금(KRW) -> 앱 결제
    balance_usd: 0, // 잔금 금액 (필드명은 레거시, 실제 화폐는 currency로 결정)
    currency: 'USD', // 잔금 화폐
    location: "", // 투어 장소
    images:[],
    price_options: { flightExcluded: false, localCostIncluded: false, cardInstallment: false },
    start_date: new Date(),
    end_date: new Date(),
    intro_desc: '',//투어를 어필할 한마디
    schedule: [
        { day: 1, date: new Date(), items: ["✈️ 인천 → 보홀 (세부퍼시픽 직항)", "🏨 리조트 체크인", "🌅 웰컴 디너 · 오리엔테이션"] },
    ],
    capacity: { min: 4, max: 8 },
    qualifications: "✅ 프리다이빙 자격증 보유자\n✅ 더딥스 입문+초급 DAY2 이상 수강생\n⚠️ 만 18세 미만은 보호자 동의 필수",
    include_items: [
        { label: "숙소 전일정",  include: true },
        { label: "보트 다이빙",  include: true },
        { label: "조식",  include: true },
        { label: "항공권",  include: false },
        { label: "여행자 보험",  include: false },
        { label: "현지 쇼핑/관광비",  include: false },
    ],
    single_room_surcharge: 0,
    detailed_content:' '
};

const toDate = (d) => (d ? new Date(d) : new Date());

const transformApiToForm = (data) => ({
    ...data,
    tour_type: data.tour_type || "LIMITED",
    name: data.name || "",
    price: data.price ? Number(data.price) : 0,
    balance_usd: data.balance_usd || 0,
    currency: data.currency || 'USD',
    location: data.location || "",
    price_options: data.price_options || { flightExcluded: false, localCostIncluded: false, cardInstallment: false },
    start_date: toDate(data.start_date),
    end_date: toDate(data.end_date),
    intro_desc: data.intro_desc || "",
    schedule: Array.isArray(data.schedule)
        ? data.schedule.map((s) => ({ ...s, date: toDate(s.date) }))
        : [],
    flight: {
        depart: {
            date: toDate(data.flight?.depart?.date),
            flightNo: data.flight?.depart?.flightNo || "",
            fromTime: data.flight?.depart?.fromTime || "07:30",
            fromCode: data.flight?.depart?.fromCode || "ICN",
            toTime: data.flight?.depart?.toTime || "12:00",
            toCode: data.flight?.depart?.toCode || "",
        },
        arrive: {
            date: toDate(data.flight?.arrive?.date),
            flightNo: data.flight?.arrive?.flightNo || "",
            fromTime: data.flight?.arrive?.fromTime || "13:00",
            fromCode: data.flight?.arrive?.fromCode || "",
            toTime: data.flight?.arrive?.toTime || "17:30",
            toCode: data.flight?.arrive?.toCode || "ICN",
        },
        notice: data.flight?.notice || "",
    },
    capacity: { min: data.min_participants || 4, max: data.max_participants || 8 },
    qualifications: data.qualifications || "",
    include_items: Array.isArray(data.include_items) ? data.include_items : [],
    single_room_surcharge: data.single_room_surcharge || 0,
    refund_policy: Array.isArray(data.refund_policy) ? data.refund_policy : [],
    refund_notice: data.refund_notice || "",
    detailed_content: data.detailed_content || " ",
});

const AdminRegistTour = () => {
    const { id } = useParams();
    
    const isEditMode = window.location.pathname.includes('edit')
    const { showSuccess, showError } = useSnackbar();
    const fileUrlMapRef = useRef(new Map());
    const nameRef = useRef(null);
    const locationRef = useRef(null);
    const priceRef = useRef(null);
    const balanceRef = useRef(null);
    // 예약금이 강사에 의해 수동 편집되었는지. true가 되면 잔금 변경 시 자동 산정을 멈춘다.
    const depositTouchedRef = useRef(false);

    const nextFocus = (ref) => (e) => {
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            ref.current?.focus();
        }
    };

    const [form, setForm] = useState(MOCK);
    const [priceInput, setPriceInput] = useState('');
    const [balanceInput, setBalanceInput] = useState('');
    const [singleRoomInput, setSingleRoomInput] = useState('');
    const [instructorInfo, setInstructorInfo] = useState(null);
    // const [existingImages, setExistingImages] = useState([]);
    const [thumbnailUrls, setThumbnailUrls] = useState([]);

    const [submitted, setSubmitted] = useState(false);
    const [changedFields, setChangedFields] = useState(new Set());
    const [showGuideInput, setShowGuideInput] = useState(false)
    const [guideInput, setGuideInput] = useState('')
    const [instructorList, setInstructorList] = useState([])
    const [guides, setGuides] = useState([])
    const [mainGuide, setMainGuide] = useState(null) // creator(메인 강사) 이름


    useEffect(() => {
        getMyInstructorProfile().then((res) => setInstructorInfo(res));
        getPublicInstructors().then((list) => setInstructorList(list || []));
        if(isEditMode){
          getPendingTravelDetail(id).then((data) => {
            if (!data) return;
            const transformed = transformApiToForm(data);
            if (!transformed.images?.length && data.legacy_images?.length) {
                transformed.images = data.legacy_images.map((i) => i.image);
            }
            setForm(transformed);
            if (Array.isArray(data.guide_names)) {
                setMainGuide(data.guide_names[0] ?? null);
                setGuides(data.guide_names.slice(1));
            }
            setPriceInput(data.price ? Number(data.price).toLocaleString('ko-KR') : '');
            setBalanceInput(formatAmountDisplay(data.balance_usd, data.currency || transformed.currency));
            setSingleRoomInput(data.single_room_surcharge ? Number(data.single_room_surcharge).toLocaleString('ko-KR') : '');
        });
        }
        else if (id) {
            getTravelProduct(id).then((data) => {
                if (!data) return;
                const transformed = transformApiToForm(data);
                if (!transformed.images?.length && data.legacy_images?.length) {
                    transformed.images = data.legacy_images.map((i) => i.image);
                }
                setForm(transformed);
                if (Array.isArray(data.guide_names)) {
                    setMainGuide(data.guide_names[0] ?? null);
                    setGuides(data.guide_names.slice(1));
                }
                setPriceInput(data.price ? Number(data.price).toLocaleString('ko-KR') : '');
                setBalanceInput(formatAmountDisplay(data.balance_usd, data.currency || transformed.currency));
                setSingleRoomInput(data.single_room_surcharge ? Number(data.single_room_surcharge).toLocaleString('ko-KR') : '');
                // 기존 예약금이 있으면 자동 산정으로 덮어쓰지 않는다.
                if (data.price) depositTouchedRef.current = true;
            });
        }
    }, [id])

    // ── 인솔강사 추가 ──────────────────────────────
    const handleOpenGuideInput = async () => {
        if (showGuideInput) {
            setShowGuideInput(false);
            setGuideInput('');
            return;
        }
        if (instructorList.length === 0) {
            const list = await getPublicInstructors();
            setInstructorList(list || []);
        }
        setShowGuideInput(true);
    };

    // 이름으로 강사 전체 프로필 정보를 해석한다 (서브 인솔강사 카드 표시용)
    const resolveGuide = (name) =>
        instructorList.find((inst) => inst.name === name) || { name };

    const handleSelectGuide = (instructor) => {
        if (guides.includes(instructor.name)) {
            showError('이미 추가된 강사입니다.');
            return;
        }
        setGuides(prev => [...prev, instructor.name]);
        markChanged('guides');
        setGuideInput('');
        setShowGuideInput(false);
    };

    // ── 핸들러 ────────────────────────────────────
    const markChanged = (...fields) => {
        if (!isEditMode) return;
        setChangedFields((prev) => {
            const next = new Set(prev);
            fields.forEach((f) => next.add(f));
            return next;
        });
    };

    const set = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        markChanged(field);
    };

    const handleTextareaKeyDown = (field) => (e) => {
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            const { selectionStart, selectionEnd } = e.target;
            const val = e.target.value;
            const newVal = val.substring(0, selectionStart) + '\n' + val.substring(selectionEnd);
            setForm((prev) => ({ ...prev, [field]: newVal }));
            markChanged(field);
            requestAnimationFrame(() => {
                e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
            });
        }
    };

    const handlePriceChange = (e) => {
        const raw = e.target.value.replace(/,/g, '');
        if (!/^\d*$/.test(raw)) return;
        // 강사가 직접 예약금을 입력하면 이후 잔금 변경에도 자동 산정하지 않는다.
        depositTouchedRef.current = true;
        setPriceInput(raw);
        setForm((prev) => ({ ...prev, price: raw ? parseInt(raw, 10) : 0 }));
        markChanged('price');
    };

    const handlePriceBlur = () => {
        setPriceInput(form.price ? form.price.toLocaleString('ko-KR') : '');
    };

    // 잔금/화폐 기준으로 예약금(KRW)을 자동 산정해 반영한다. (수동 편집 전까지)
    const applyAutoDeposit = (balance, currency) => {
        if (depositTouchedRef.current) return;
        const deposit = calcDepositFromBalance(balance, currency);
        setForm((prev) => ({ ...prev, price: deposit }));
        setPriceInput(deposit ? deposit.toLocaleString('ko-KR') : '');
        if (deposit) markChanged('price');
    };

    const handleBalanceChange = (e) => {
        const el = e.target;
        const raw = el.value.replace(/,/g, '');
        if (!/^\d*\.?\d*$/.test(raw)) return;
        // 캐럿 앞쪽 숫자(+점) 개수를 세어 콤마 재배치 후에도 위치를 보존한다.
        const caret = el.selectionStart ?? raw.length;
        const nonCommaBeforeCaret = el.value.slice(0, caret).replace(/,/g, '').length;
        const formatted = formatAmountInput(raw, form.currency);
        setBalanceInput(formatted);
        // 저장값은 화폐 소수 자릿수가 반영된 표시값과 동일하게 맞춘다.
        const balance = parseFloat(formatted.replace(/,/g, '')) || 0;
        setForm((prev) => ({ ...prev, balance_usd: balance }));
        markChanged('balance_usd');
        applyAutoDeposit(balance, form.currency);
        // 포맷 적용 후 캐럿 복원
        requestAnimationFrame(() => {
            let seen = 0, pos = 0;
            while (pos < formatted.length && seen < nonCommaBeforeCaret) {
                if (formatted[pos] !== ',') seen += 1;
                pos += 1;
            }
            el.setSelectionRange(pos, pos);
        });
    };

    const handleCurrencyChange = (e) => {
        const currency = e.target.value;
        setForm((prev) => ({ ...prev, currency }));
        markChanged('currency');
        // 화폐 변경 시 표시 형식(소수 자릿수)도 새 화폐 기준으로 다시 맞춘다.
        setBalanceInput(formatAmountDisplay(form.balance_usd, currency));
        applyAutoDeposit(form.balance_usd, currency);
    };

    const handleBalanceBlur = () => {
        setBalanceInput(formatAmountDisplay(form.balance_usd, form.currency));
    };

    const handleSingleRoomChange = (e) => {
        const raw = e.target.value.replace(/,/g, '');
        if (!/^\d*$/.test(raw)) return;
        setSingleRoomInput(raw);
        setForm((prev) => ({ ...prev, single_room_surcharge: raw ? parseInt(raw, 10) : 0 }));
        markChanged('single_room_surcharge');
    };

    const handleSingleRoomBlur = () => {
        setSingleRoomInput(form.single_room_surcharge ? form.single_room_surcharge.toLocaleString('ko-KR') : '');
    };

    // 배열 아이템 수정 (field 없으면 문자열 배열, 있으면 객체 배열)
    const setArrItem = (arr, idx, field) => (e) => {
        setForm((prev) => {
            const next = [...prev[arr]];
            next[idx] = field ? { ...next[idx], [field]: e.target.value } : e.target.value;
            return { ...prev, [arr]: next };
        });
        markChanged(arr);
    };

    const addArrItem = (arr, template) => {
        setForm((prev) => ({ ...prev, [arr]: [...prev[arr], template] }));
        markChanged(arr);
    };

    const removeArrItem = (arr, idx) => {
        setForm((prev) => ({ ...prev, [arr]: prev[arr].filter((_, i) => i !== idx) }));
        markChanged(arr);
    };

    // 일정 핸들러
    const setScheduleDate = (sIdx) => (date) => {
        setForm((prev) => {
            const next = [...prev.schedule];
            next[sIdx] = { ...next[sIdx], date };
            return { ...prev, schedule: next };
        });
        markChanged('schedule');
    };

    const setScheduleItem = (sIdx, iIdx) => (e) => {
        setForm((prev) => {
            const next = [...prev.schedule];
            const items = [...next[sIdx].items];
            items[iIdx] = e.target.value;
            next[sIdx] = { ...next[sIdx], items };
            return { ...prev, schedule: next };
        });
        markChanged('schedule');
    };

    const addScheduleItem = (sIdx) => {
        setForm((prev) => {
            const next = [...prev.schedule];
            next[sIdx] = { ...next[sIdx], items: [...next[sIdx].items, ""] };
            return { ...prev, schedule: next };
        });
        markChanged('schedule');
    };

    const removeScheduleItem = (sIdx, iIdx) => {
        setForm((prev) => {
            const next = [...prev.schedule];
            next[sIdx] = { ...next[sIdx], items: next[sIdx].items.filter((_, i) => i !== iIdx) };
            return { ...prev, schedule: next };
        });
        markChanged('schedule');
    };

    const addScheduleDay = () => {
        setForm((prev) => {
            const last = prev.schedule[prev.schedule.length - 1];
            const nextDate = last?.date
                ? new Date(last.date.getTime() + 24 * 60 * 60 * 1000)
                : new Date();
            return {
                ...prev,
                schedule: [...prev.schedule, { day: prev.schedule.length + 1, date: nextDate, items: [""] }],
            };
        });
        markChanged('schedule');
    };

    const removeScheduleDay = (idx) => {
        setForm((prev) => ({ ...prev, schedule: prev.schedule.filter((_, i) => i !== idx) }));
        markChanged('schedule');
    };

    // 비용 포함/별도 토글
    const toggleCostExtra = (idx) => {
        setForm((prev) => {
            const next = [...prev.include_items];
            const isInclude = next[idx].include;
            next[idx] = { ...next[idx], include: !isInclude };
            return { ...prev, include_items: next };
        });
        markChanged('include_items');
    };

    const togglePriceOption = (key) => {
        setForm((prev) => ({
            ...prev,
            price_options: { ...prev.price_options, [key]: !prev.price_options[key] },
        }));
        markChanged('price_options');
    };

    const stepCapacity = (field, delta) => {
        setForm((prev) => ({
            ...prev,
            capacity: { ...prev.capacity, [field]: Math.max(1, prev.capacity[field] + delta) },
        }));
        markChanged('capacity');
    };

    const errCls = (val) => submitted && !String(val ?? '').trim() ? ' td-editable--error' : '';

    const fmt = (n) => Number(n).toLocaleString("ko-KR");

    const fmtDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : null;

    // 투어 상세 페이지 리치 에디터 본문(HTML) 변경
    const handleDetailedContentChange = (html) => {
        setForm((prev) => ({ ...prev, detailed_content: html }));
        markChanged('detailed_content');
    };

    // 리치 에디터 이미지 업로드: 파일을 서버에 올리고 접근 가능한 URL 반환
    const uploadDetailImage = async (file) => {
        const fd = new FormData();
        fd.append('image', file);
        const res = await api.post('/buccl_main/api/v1/upload-image/travel-product/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data.url;
    };

    const handleThumbnailsChange = async (files, existingUrls = []) => {
        // 새로 추가된 파일 업로드
        for (const file of files) {
            if (!fileUrlMapRef.current.has(file)) {
                const fd = new FormData();
                fd.append('image', file);
                const res = await api.post('/buccl_main/api/v1/upload-image/travel-product/', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                fileUrlMapRef.current.set(file, res.data.url);
            }
        }
        // 삭제된 파일 제거
        for (const file of [...fileUrlMapRef.current.keys()]) {
            if (!files.includes(file)) {
                fileUrlMapRef.current.delete(file);
            }
        }
        // 기존 URL + 새로 업로드된 URL 합산
        const newUrls = files.map((f) => fileUrlMapRef.current.get(f)).filter(Boolean);
        setThumbnailUrls([...existingUrls, ...newUrls]);
        markChanged('images');
    };

    const handleSubmit = async () => {
        setSubmitted(true);

        const checks = [
            { label: '투어명', invalid: !form.name.trim() },
            { label: '장소', invalid: !form.location.trim() },
            { label: '썸네일', invalid: thumbnailUrls.length === 0 },
            // { label: '강사 한마디', invalid: !form.intro_desc.trim() },
            { label: '참가 자격', invalid: !form.qualifications.trim() },
            ...(isLimited ? [
                // { label: '항공편 정보', invalid: !form.flight.depart.flightNo.trim() || !form.flight.depart.fromCode.trim() || !form.flight.depart.toCode.trim() || !form.flight.arrive.flightNo.trim() || !form.flight.arrive.fromCode.trim() || !form.flight.arrive.toCode.trim() },
                // { label: '항공 안내', invalid: !form.flight.notice.trim() },
                { label: '일정 항목', invalid: form.schedule.some((s) => s.items.some((item) => !item.trim())) },
            ] : []),
            { label: '비용 항목명', invalid: form.include_items.some((c) => !c.label.trim()) },
        ];
        const missing = checks.filter((c) => c.invalid).map((c) => c.label);

        if (missing.length > 0 && !isEditMode) {
            showError(`${missing.join(', ')}을(를) 입력해주세요.`);
            return;
        }

        try {
            if (isEditMode) {
                const cf = changedFields;
                const editBody = {};

                ['name', 'location', 'balance_usd', 'currency', 'intro_desc', 'qualifications',
                    'tour_type', 'price_options', 'include_items', 'detailed_content'].forEach((f) => {
                    if (cf.has(f)) editBody[f] = form[f];
                });
                if (cf.has('guides')) editBody.guide_names = [mainGuide ?? instructorInfo?.name, ...guides].filter(Boolean);
                if (cf.has('price')) editBody.price = form.price;
                if (cf.has('single_room_surcharge')) editBody.single_room_surcharge = form.single_room_surcharge;
                if (cf.has('images')) editBody.images = [...thumbnailUrls];
                if (cf.has('capacity')) {
                    editBody.min_participants = form.capacity.min;
                    editBody.max_participants = form.capacity.max;
                }
                if (cf.has('start_date')) editBody.start_date = fmtDate(form.start_date);
                if (cf.has('end_date')) editBody.end_date = fmtDate(form.end_date);
                if (cf.has('schedule')) {
                    editBody.schedule = form.schedule.map((s) => ({
                        day: s.day,
                        date: fmtDate(s.date),
                        items: s.items,
                    }));
                }

                await updateTravelProduct(id, editBody);
                showSuccess('투어 정보가 수정되었습니다.');
            } else {
                const body = {
                    ...form,
                    images: [...thumbnailUrls],
                    guide_names: [instructorInfo.name, ...guides],
                    min_participants: form.capacity.min,
                    max_participants: form.capacity.max,
                    ...(form.tour_type === 'LIMITED' ? {
                        start_date: fmtDate(form.start_date),
                        end_date: fmtDate(form.end_date),
                        schedule: form.schedule.map((s) => ({
                            day: s.day,
                            date: fmtDate(s.date),
                            items: s.items,
                        })),
                    } : {
                        start_date: fmtDate(form.start_date),
                        end_date: fmtDate(form.end_date),
                    }),
                };
                await api.post('/buccl_main/api/v1/travel-products/create/', body);
                showSuccess('투어 개설을 신청하였습니다. 관리자 승인이 완료되면 목록에 노출됩니다.');
            }
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || '투어 등록에 실패했습니다';
            showError(message);
        }
    };

    const isLimited = form.tour_type === 'LIMITED';

    return (
        <div className="tour-detail-page">
            <SubHeader title={isEditMode ? "투어 수정" : "투어 개설"} />
            {/* 투어 유형 */}
            <div className="td-section td-section--type">
                <div className="td-section-title">투어 유형</div>
                <div className="td-type-toggle">
                    {[
                        { value: 'LIMITED', label: '기간 한정', desc: '특정 날짜에 운영되는 투어' },
                        { value: 'ALWAYS', label: '상시 운영', desc: '날짜 무관 항상 신청 가능' },
                    ].map(({ value, label, desc }) => (
                        <label key={value} className={`td-type-option${form.tour_type === value ? ' td-type-option--active' : ''}`}>
                            <input
                                type="radio"
                                name="tourType"
                                value={value}
                                checked={form.tour_type === value}
                                onChange={() => { setForm((prev) => ({ ...prev, tour_type: value })); markChanged('type'); }}
                            />
                            <div className="td-type-option-text">
                                <span className="td-type-option-label">{label}</span>
                                <span className="td-type-option-desc">{desc}</span>
                            </div>
                        </label>
                    ))}
                </div>
            </div>

            <div className="td-section td-section--upload">
                <div className="td-section-title">대표사진/썸네일 등록</div>
                <p className="td-section-sub">이미지는 가급적 가로*세로 3:2, 이미지 내 문구는 중앙에 맞춰주셔야 잘리지 않습니다.</p>
                <div className={submitted && thumbnailUrls.length === 0 && !isEditMode ? 'td-upload-error' : ''}>
                    <MultiImageUpload
                        onImagesUploaded={handleThumbnailsChange}
                        initialImages={form.images}
                    />
                </div>
            </div>

            {/* ① 핵심 정보 */}
            <div id="td-info" className="td-section td-section--info">
                <div className="td-section-title">필수 정보</div>
                <input
                    ref={nameRef}
                    className={`td-editable td-editable--title${errCls(form.name)}`}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="투어 제목을 입력하세요"
                    enterKeyHint="next"
                    onKeyDown={nextFocus(locationRef)}
                />
                <div className="td-location-wrap">
                    <span className="td-location-pin">📍</span>
                    <input
                        ref={locationRef}
                        className={`td-editable td-editable--location${errCls(form.location)}`}
                        value={form.location}
                        onChange={set("location")}
                        placeholder="어디로 가나요? (예: 필리핀 보홀)"
                        enterKeyHint="next"
                        onKeyDown={nextFocus(balanceRef)}
                    />
                </div>
                <div className="td-price-group">
                  <div>
                      <div className="td-price-group-label">강사 프로그램 비용</div>
                      <div className="td-price-input-wrap td-price-input-wrap--edit">
                          <select
                              className="td-currency-select"
                              value={form.currency}
                              onChange={handleCurrencyChange}
                          >
                              {CURRENCY_OPTIONS.map((opt) => (
                                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                              ))}
                          </select>
                          {getCurrencyConfig(form.currency).prefix && (
                              <span className="td-price-unit td-price-unit--prefix">{getCurrencyConfig(form.currency).symbol}</span>
                          )}
                          <input ref={balanceRef} className="td-price-input" type="text" inputMode="decimal" value={balanceInput} onChange={handleBalanceChange} onBlur={handleBalanceBlur} enterKeyHint="next" onKeyDown={nextFocus(priceRef)} />
                          {!getCurrencyConfig(form.currency).prefix && (
                              <span className="td-price-unit">{getCurrencyConfig(form.currency).unit}</span>
                          )}
                      </div>
                  </div>
                  <div >
                      <div className="td-price-group-label">예약금</div>
                      <div className="td-price-input-wrap td-price-input-wrap--edit">
                          <input ref={priceRef} className="td-price-input" type="text" inputMode="numeric" value={priceInput} onChange={handlePriceChange} onBlur={handlePriceBlur} enterKeyHint="done" />
                          <span className="td-price-unit">원</span>
                      </div>
                  </div>
                </div>
              
                {isLimited && (
                    <div className="td-option-checks">
                        {[
                            { key: "flightExcluded", label: "항공 별도" },
                            { key: "localCostIncluded", label: "현지 비용 포함" },
                            { key: "cardInstallment", label: "카드 할부 가능" },
                        ].map(({ key, label }) => (
                            <label key={key} className="td-option-check">
                                <input type="checkbox" checked={form.price_options[key]} onChange={() => togglePriceOption(key)} />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* ② 인솔강사 */}
            {instructorInfo && 
                <div id="td-instructor" className="td-section">
                <div className="td-section-title">
                  인솔 강사 정보
                </div>
            
                <div className="td-instructor-profile">
                    <div className="td-instructor-av">
                        {instructorInfo.profile_image_url && (
                            <img
                                className="td-instructor-av-img"
                                src={instructorInfo.profile_image_url}
                                alt={instructorInfo.name}
                                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                            />
                        )}
                        <span style={{ display: instructorInfo.profile_image_url ? "none" : "flex" }}>🧑‍🏫</span>
                    </div>
                    <div className="td-instructor-info">
                        <div className="td-instructor-name">{instructorInfo.name}</div>
                        <div className="td-instructor-cert">
                            {[instructorInfo.brand_name, instructorInfo.contact_phone, instructorInfo.contact_email].filter(Boolean).join(" · ")}
                        </div>
                    </div>
                </div>
                {guides.map((name, i) => {
                  const g = resolveGuide(name);
                  return (
                    <div key={i} className="td-instructor-profile td-instructor-profile--guide">
                        <div className="td-instructor-av">
                            {g.profile_image_url && (
                                <img
                                    className="td-instructor-av-img"
                                    src={g.profile_image_url}
                                    alt={g.name}
                                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                                />
                            )}
                            <span style={{ display: g.profile_image_url ? "none" : "flex" }}>🧑‍🏫</span>
                        </div>
                        <div className="td-instructor-info">
                            <div className="td-instructor-name">{g.name}</div>
                            <div className="td-instructor-cert">
                                {[g.brand_name, g.contact_phone, g.contact_email].filter(Boolean).join(" · ")}
                            </div>
                        </div>
                        <button
                          type="button"
                          className="td-guide-remove td-guide-remove--card"
                          onClick={() => { setGuides(prev => prev.filter((_, idx) => idx !== i)); markChanged('guides'); }}
                        >✕</button>
                    </div>
                  );
                })}
                <div className="td-guide-add-row">
                  <button
                    type="button"
                    className="btn btn-fit btn-xs "
                    onClick={handleOpenGuideInput}
                  >
                    인솔강사 추가 +
                  </button>
                </div>
                {showGuideInput && (
                  <div className="td-guide-input-wrap">
                    <input
                      type="text"
                      className="td-editable td-guide-search-input"
                      placeholder="강사 이름 검색"
                      value={guideInput}
                      onChange={(e) => setGuideInput(e.target.value)}
                      autoFocus
                    />
                    {guideInput.trim() && instructorList.filter(inst =>
                      inst.name.includes(guideInput) &&
                      !guides.includes(inst.name) &&
                      inst.name !== instructorInfo?.name
                    ).map(inst => (
                      <div
                        key={inst.user_id}
                        className="td-guide-dropdown-item"
                        onClick={() => handleSelectGuide(inst)}
                      >
                        <div className="td-guide-dropdown-info">
                          <span className="td-guide-dropdown-name">{inst.name}</span>
                          {inst.brand_name && <span className="td-guide-dropdown-brand">{inst.brand_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>}


            <div className="td-section">
                <div className="td-section-title">
                  투어 정보
                </div>
                <div className="td-instructor-quote">
                    <textarea
                        className={`td-editable td-editable--textarea${errCls(form.intro_desc)}`}
                        value={form.intro_desc}
                        onChange={set("intro_desc")}
                        onKeyDown={handleTextareaKeyDown("intro_desc")}
                        placeholder="투어를 어필할 강사의 한마디 (ex.보홀의 바다는 처음 프리다이빙을 시작하는 분들에게 완벽한 곳입니다.)"
                        rows={3}
                    />
                </div>
            </div>

            {/* ③ 투어 기간 선택 */}
            { <div className="td-section td-section--dates">
                <div className="td-section-title">투어 기간</div>
                <div className="td-date-row">
                    <div className="td-date-field">
                        <span className="td-date-label">출발 날짜</span>
                        <DatePicker
                            selected={form.start_date}
                            onChange={(date) => {            
                                setForm((prev) => {
                                    const nextSchedule = [...prev.schedule];
                                    if (nextSchedule.length > 0) {
                                        nextSchedule[0] = { ...nextSchedule[0], date };
                                    }
                                    return {
                                        ...prev,
                                        start_date: date,
                                        schedule: nextSchedule,
                                    };
                                });
                                markChanged('start_date', 'schedule');
                            }}
                            selectsStart
                            startDate={form.start_date}
                            endDate={form.end_date}
                            dateFormat="yyyy.MM.dd"
                            placeholderText="출발일 선택"
                            className="td-datepicker-input"
                        />
                    </div>
                    <>
                      <span className="td-date-sep">~</span>
                    <div className="td-date-field">
                        <span className="td-date-label">귀국 날짜</span>
                        <DatePicker
                            selected={form.end_date}
                            onChange={(date) => {
                                setForm((prev) => ({ ...prev, end_date: date }));
                                markChanged('end_date');
                            }}
                            selectsEnd
                            startDate={form.start_date}
                            // endDate={form.end_date}
                            minDate={form.start_date}
                            dateFormat="yyyy.MM.dd"
                            placeholderText="도착일 선택"
                            className="td-datepicker-input"
                        />
                    </div>
                    </>
             
                </div>
            </div>}

            {/* ④ 투어 일정 */}
            {isLimited && <div id="td-schedule" className="td-section">
                <div className="td-section-title">투어 일정</div>
                {form.schedule.map((s, sIdx) => (
                    <div key={sIdx} className="td-schedule-item">
                        <div className="td-schedule-left">
                            <div className={`td-day-badge${sIdx === form.schedule.length - 1 ? " td-day-badge--gray" : ""}`}>D{s.day}</div>
                            {sIdx < form.schedule.length - 1 && <div className="td-day-line" />}
                        </div>
                        <div className="td-schedule-body">
                            <div className="td-editable-row">
                                <DatePicker
                                    selected={s.date}
                                    onChange={setScheduleDate(sIdx)}
                                    dateFormat="M/d (eee)"
                                    className="td-editable td-editable--schedule-date td-datepicker-input td-datepicker-input--sm"
                                    placeholderText="날짜 선택"
                                />
                                <button className="td-btn-remove" onClick={() => removeScheduleDay(sIdx)}>✕</button>
                            </div>
                            {s.items.map((item, iIdx) => (
                                <div key={iIdx} className="td-editable-row">
                                    <input className={`td-editable td-editable--schedule-item${errCls(item)}`} value={item} onChange={setScheduleItem(sIdx, iIdx)} placeholder="일정 항목" />
                                    {iIdx === s.items.length -1 ?
                                      <button className="td-btn-remove" onClick={() => addScheduleItem(sIdx)} style={{fontSize:'16px', color: '#6c5ce7'}}>+</button>
                                      :   
                                      <button className="td-btn-remove" onClick={() => removeScheduleItem(sIdx, iIdx)}>✕</button>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <button className="td-btn-add" onClick={addScheduleDay}>+ 일정</button>
            </div>}


            {/* ⑤ 모집 인원 & 참가 자격 */}
            <div className="td-section">
                <div className="td-section-title">모집 인원 & 참가 자격</div>
                <div className="td-capacity-row">
                    <div className="td-capacity-card td-capacity-card--purple">
                        <div className="td-capacity-label">최소 출발</div>
                        <div className="td-stepper">
                            <button className="btn td-stepper-btn" onClick={() => stepCapacity("min", -1)}>−</button>
                            <span className="td-stepper-val">{form.capacity.min}</span>
                            <button className="btn td-stepper-btn" onClick={() => stepCapacity("min", 1)}>+</button>
                        </div>
                    </div>
                    <div className="td-capacity-card td-capacity-card--purple">
                        <div className="td-capacity-label">최대 인원</div>
                        <div className="td-stepper">
                            <button className="btn td-stepper-btn" onClick={() => stepCapacity("max", -1)}>−</button>
                            <span className="td-stepper-val">{form.capacity.max}</span>
                            <button className="btn td-stepper-btn" onClick={() => stepCapacity("max", 1)}>+</button>
                        </div>            
                    </div>
                </div>
                <div className="td-qualify-box">
                    <div className="td-qualify-title">참가 자격 요건</div>
                    <textarea className={`td-editable td-editable--textarea${errCls(form.qualifications)}`} value={form.qualifications} onChange={set("qualifications")} onKeyDown={handleTextareaKeyDown("qualifications")} rows={5} />
                </div>
            </div>


            <div className="td-section">
                <div className="td-section-title">투어 추가 정보</div>
                <p className="td-section-sub">투어 상세 안내, 숙소, 다이빙 포인트 등 설명과 이미지를 자유롭게 작성하세요.</p>
                <RichTextEditor
                    value={form.detailed_content}
                    onChange={handleDetailedContentChange}
                    uploadImage={uploadDetailImage}
                    placeholder="투어 상세 설명을 입력하세요. 이미지는 툴바 또는 드래그·붙여넣기로 추가할 수 있습니다."
                />
            </div>

            {/* ⑥ 투어 비용 */}
            <div id="td-cost" className="td-section">
                <div className="td-section-title">투어 비용</div>
                <div className="td-cost-table">
                    <div className="td-cost-row td-cost-row--header">
                        <span className="td-cost-label td-cost-label--brand">투어 예약금</span>
                        <span className="td-cost-value td-cost-value--brand">{fmt(form.price)}원</span>
                    </div>
                    {form.include_items.map((c, i) => (
                        <div key={i} className="td-cost-row">
                            <input className={`td-editable td-editable--cost-label${errCls(c.label)}`} value={c.label} onChange={setArrItem("include_items", i, "label")} placeholder="항목명" />
                            <div className="td-cost-actions">
                                <div className={`td-cost-switch${c.include ? "" : " td-cost-switch--extra"}`} onClick={() => toggleCostExtra(i)}>
                                    <span className="td-cost-switch-opt td-cost-switch-opt--in">포함</span>
                                    <span className="td-cost-switch-opt td-cost-switch-opt--ex">별도</span>
                                </div>
                                <button className="td-btn-remove" onClick={() => removeArrItem("include_items", i)}>✕</button>
                            </div>
                        </div>
                    ))}
                </div>
                <button className="td-btn-add" onClick={() => addArrItem("include_items", { label: "", include: false })}>+ 항목 추가</button>
                <div className="td-single-room">
                    <span className="td-single-room-label">1인실 변경 시 추가 비용</span>
                    <div className="td-single-room-input">
                        <span>+</span>
                        <input className="td-editable td-editable--num" type="text" inputMode="numeric" value={singleRoomInput} onChange={handleSingleRoomChange} onBlur={handleSingleRoomBlur} />
                        <span>원</span>
                    </div>
                </div>
            </div>

            <div className="btn-wrap btn-bottom-fixed">
                <button className="btn bnt-full td-cta-btn" onClick={handleSubmit}>{isEditMode ? "수정하기" : "등록하기"}</button>
            </div>
        </div>
    );
};

export default AdminRegistTour;


